// The captain's torpedo watch: which enemy fish in the water are coming for
// her, and the line the warning banner gives them. Split out of main.js's
// frame loop so the rule can be pinned without a browser
// (client/test/torpwatch.test.mjs).
//
// The banner carries one line, and "⚠ TORPEDOES ⚠" outranks everything on
// it. It shipped lighting for ANY enemy fish closing inside 900 m, whether or
// not its track came near her: a destroyer in the thick of a fight wore it for
// a quarter to a third of her sortie, mostly for spreads that would pass
// hundreds of metres off, and a captain dodging a spread onto a beach was told
// about the fish and never about the rocks. So the watch now asks what a
// lookout asks — will that track meet her, before an island meets it? — and a
// shoal or a grounding shares the line with the fish instead of hiding under
// it.

// How far off a fish is sighted and called (m): the old trigger's reach. At
// 72 m/s a destroyer's spread is ~12 s out; a torpedo bomber's slower fish
// half a minute.
export const TORP_SIGHT_M = 900;

// The warning owed for any fish that will strike her, whatever she does with
// the helm in the meantime (s). The watch calls every fish whose track meets
// the water she could reach in that long — the rudder hard over either way,
// the telegraph rung up or down — so a fish she's turning into is called
// while there's still time to take the turn off, and one aimed at her present
// course is called the moment it's sighted.
export const TORP_LEAD_SEC = 6;

// How long the banner holds a called fish after the watch last saw it coming
// (s), so a track grazing the edge of her water can't strobe the line or
// re-sound the alarm on every frame.
export const TORP_HOLD_SEC = 1;

// What the client can't know exactly (m): where she and the fish are is a
// snapshot old and eased, and her way is read off her own track.
const TORP_SLOP_M = 25;

// How far ahead a fish is followed (s): past this, even one heading straight
// for her is a story for later. A torpedo bomber's 24 m/s fish needs 37 s to
// run in from the edge of sight onto a hull lying stopped; one chasing a
// Fletcher making flank closes at 3 m/s, and is called once it's that near.
export const TORP_HORIZON_SEC = 40;

// The step a fish is followed in (s). A destroyer's fish meeting a Fletcher bow
// on at flank closes 93 m/s, 9 m a step: well inside the narrowest water the
// watch guards (her beam and the slop, both sides of her).
const TORP_STEP_SEC = 0.1;

// A running fish holds its depth; one still falling from a torpedo bomber
// hasn't a run to judge yet (it keeps its heading and sheds its speed when it
// meets the water, a second or three later).
const TORP_RUNNING_VY = 0.5;

// The step a called fish's run is sounded for land in (m): the ground's own
// grid. An island stops a fish dead (torpedo.go beaches it the tick it meets
// dry ground), and the Brick Sea is full of them; a spit narrow enough to
// slip between soundings only costs a call that wasn't needed.
const TORP_LAND_STEP_M = 12;

/** How far she could stray from the water her present course and way put her
 *  in, t seconds on (t ≤ TORP_LEAD_SEC): { along, abeam } in metres, what the
 *  hull box the watch guards grows by. Her helm and engines as the server's
 *  shipIntegrate turns them: the telegraph gains or sheds her accelMSS a
 *  second, and the rudder bites harder with way on (a fifth of its bite at a
 *  standstill). A hard turn carries her middle off her line and swings bow
 *  and stern across it, and costs her way along it. */
export function torpedoReach(way, type, t) {
  const hl = hullHalfLen(type), hb = hullHalfBeam(type);
  const flank = type?.speedMS > 0 ? type.speedMS : 16;
  const accel = type?.accelMSS > 0 ? type.accelMSS : 0.5;
  const rate = type?.turnRateRadPerSec > 0 ? type.turnRateRadPerSec : 0.08;
  const run = Math.abs(way);
  // The most way she can carry by then, and the rate her rudder turns her at it.
  const s = Math.min(Math.max(flank, run), run + accel * t);
  const w = rate * (0.2 + 0.8 * Math.min(1, s / flank));
  const quarter = Math.PI / 2 / w; // seconds to swing ninety degrees
  const th = Math.min(w * t, Math.PI / 2);
  const r = s / w;
  // Her middle off her line: round the turn, then straight on across it.
  const off = t <= quarter ? r * (1 - Math.cos(th)) : r + s * (t - quarter);
  // The way the turn costs her along it, and what the telegraph adds or sheds.
  const short = t <= quarter ? s * t - r * Math.sin(th) : s * t - r;
  return {
    along: 0.5 * accel * t * t + short + hb * Math.sin(th),
    abeam: off + hl * Math.sin(th),
  };
}

// ships.go navalHullBox: half her keel and half her beam, or her hit radius.
const hullHalfLen = (type) => (type?.hullHalfLenM > 0 ? type.hullHalfLenM : type?.hitRadiusM ?? 10);
const hullHalfBeam = (type) => (type?.hullHalfBeamM > 0 ? type.hullHalfBeamM : (type?.hitRadiusM ?? 10) * 0.4);

/** Seconds until a fish at (x, z) running at (vx, vz) m/s reaches the water
 *  she could be in by then, or null if its track clears her. `hull` is
 *  { x, z, yaw, way }: where she turns about, her heading (the house yaw),
 *  and her way along it in m/s (negative astern); `type` her class. The fish
 *  run dead straight (torpedo.go); she's taken on at her present course and
 *  way, in the hull box the server buries a fish in (navalHullHit), grown by
 *  the slop and by what her helm can do in the seconds the fish needs. */
export function torpedoEta(hull, type, x, z, vx, vz) {
  const hl = hullHalfLen(type), hb = hullHalfBeam(type);
  const fx = Math.cos(hull.yaw), fz = -Math.sin(hull.yaw);
  // The fish in her frame, riding with her: along her keel and abeam (the
  // server's axes), and how fast each closes.
  const rx = x - hull.x, rz = z - hull.z;
  const wx = vx - fx * hull.way, wz = vz - fz * hull.way;
  const a0 = rx * fx + rz * fz, b0 = -rx * fz + rz * fx;
  const av = wx * fx + wz * fz, bv = -wx * fz + wz * fx;
  const far = torpedoReach(hull.way, type, TORP_LEAD_SEC);
  const maxA = hl + TORP_SLOP_M + far.along, maxB = hb + TORP_SLOP_M + far.abeam;
  // A track that never comes inside the widest water she guards is no threat:
  // most fish stop here.
  const v2 = av * av + bv * bv;
  const tc = v2 > 1e-9 ? Math.min(TORP_HORIZON_SEC, Math.max(0, -(a0 * av + b0 * bv) / v2)) : 0;
  const ca = a0 + av * tc, cb = b0 + bv * tc;
  if (ca * ca + cb * cb > maxA * maxA + maxB * maxB) return null;
  for (let t = 0; t <= TORP_HORIZON_SEC + 1e-9; t += TORP_STEP_SEC) {
    const m = t < TORP_LEAD_SEC ? torpedoReach(hull.way, type, t) : far;
    if (Math.abs(a0 + av * t) <= hl + TORP_SLOP_M + m.along && Math.abs(b0 + bv * t) <= hb + TORP_SLOP_M + m.abeam) {
      return t;
    }
  }
  return null;
}

/** Does a fish at (x, z) running at (vx, vz) meet dry ground within `run`
 *  metres? `dry(x, z)` says whether the ground there stands above the water
 *  line (main.js: the field's surfaceAt, the triangles the server beaches a
 *  fish on). */
export function torpedoBeaches(dry, x, z, vx, vz, run) {
  const s = Math.hypot(vx, vz);
  if (s < 1e-6) return false;
  const ux = vx / s, uz = vz / s;
  for (let d = TORP_LAND_STEP_M; d <= run; d += TORP_LAND_STEP_M) {
    if (dry(x + ux * d, z + uz * d)) return true;
  }
  return false;
}

/** The fish coming for her soonest: { i, eta } — its slot in the ordnance pool
 *  (effects.js Bombs: `count` and the `f` arrays px, pz, vx, vy, vz) and the
 *  seconds until it reaches her — or null. `isFoe(i)` picks the enemy
 *  torpedoes out of everything else the pool carries; `dry(x, z)`, when
 *  given, lets the watch pass over a fish an island will stop short of her
 *  water (see torpedoBeaches). */
export function incomingTorpedo(hull, type, pool, isFoe, dry = null) {
  const { px, pz, vx, vy, vz } = pool.f;
  let best = null;
  let calls = null; // with land to sound: every fish whose run meets her water
  for (let i = 0; i < pool.count; i++) {
    if (!isFoe(i) || Math.abs(vy[i]) >= TORP_RUNNING_VY) continue;
    const dx = px[i] - hull.x, dz = pz[i] - hull.z;
    if (dx * dx + dz * dz > TORP_SIGHT_M * TORP_SIGHT_M) continue;
    const eta = torpedoEta(hull, type, px[i], pz[i], vx[i], vz[i]);
    if (eta === null) continue;
    if (dry) (calls ??= []).push({ i, eta });
    else if (best === null || eta < best.eta) best = { i, eta };
  }
  if (!dry || !calls) return best;
  // Sound them soonest first, stopping at the first with open water to her:
  // most frames there's one fish or none, and a spread that beaches beaches
  // together.
  calls.sort((a, b) => a.eta - b.eta);
  for (const c of calls) {
    if (!torpedoBeaches(dry, px[c.i], pz[c.i], vx[c.i], vz[c.i], Math.hypot(vx[c.i], vz[c.i]) * c.eta)) return c;
  }
  return null;
}

/** The watch: which fish the banner is calling, and for which hull. main.js
 *  keeps one and drives it every frame a warship's captain has the helm. */
export class TorpedoWatch {
  constructor() {
    this.hullId = null; // the hull the watch is keeping (null: off watch)
    this.until = -Infinity; // the banner holds the last call until then (seconds, the caller's clock)
    this.threat = null; // the fish called: { x, z, eta } where it was when last seen coming
    this.calls = 0; // fresh calls (the alarm sounds on each); probes read it off window.__va.torpWatch
  }

  /** One frame at the helm: `now` in SECONDS (perfNow). Returns the fish the
   *  banner should call, or null. A new hull starts with a clear sea: no
   *  life inherits the last one's call. */
  update(now, hullId, hull, type, pool, isFoe, dry = null) {
    if (hullId !== this.hullId) this.standDown();
    this.hullId = hullId;
    const hit = incomingTorpedo(hull, type, pool, isFoe, dry);
    if (hit) {
      if (now >= this.until) this.calls++;
      this.until = now + TORP_HOLD_SEC;
      this.threat = { x: pool.f.px[hit.i], z: pool.f.pz[hit.i], eta: hit.eta };
    }
    return now < this.until ? this.threat : null;
  }

  /** Off watch: sunk, off the bridge, or a hull no fish can strike. */
  standDown() {
    this.hullId = null;
    this.until = -Infinity;
    this.threat = null;
    return null;
  }
}

/** The banner's line for a called fish: its bearing (an 8-point compass
 *  word, or "" when there's none to give) and the navigation hazard live at
 *  the same moment ("SHOAL AHEAD", "RUN AGROUND", or "") sharing the line, so
 *  a captain combing a spread is told about the rocks too. As short as the
 *  words allow: on a phone the banner rides between the SPD gauge and the
 *  minimap. */
export function torpedoLine(bearing, hazard = "") {
  const fish = bearing ? `TORPEDOES — ${bearing}` : "TORPEDOES";
  return hazard ? `⚠ ${fish} · ${hazard} ⚠` : `⚠ ${fish} ⚠`;
}
